import {ToCNode} from "../../../../Types/ToCNode";
import {TaskType} from "../../../../Types/Task";

/** Recursively collect all leaf IDs from a ToCNode tree. */
export function getLeafIds(node: ToCNode): string[] {
    if (!node.Children || node.Children.length === 0) return [node.Id];
    return node.Children.flatMap(getLeafIds);
}

/** Get all leaf IDs for a task (from its ToC root node). */
export function getTaskLeafIds(task: TaskType): string[] {
    if (!task.ToC) return [];
    return getLeafIds(task.ToC);
}

/**
 * Remove specified leaf IDs from a ToC tree.
 * Prunes parent branches that have no remaining children.
 * Returns null if the entire tree is empty after pruning.
 */
export function pruneToC(node: ToCNode, idsToRemove: Set<string>): ToCNode | null {
    const isLeaf = !node.Children || node.Children.length === 0;

    if (isLeaf) {
        return idsToRemove.has(node.Id) ? null : {...node, Children: []};
    }

    const remainingChildren = node.Children
        .map((child) => pruneToC(child, idsToRemove))
        .filter((child): child is ToCNode => child !== null);

    if (remainingChildren.length === 0) return null;

    return {...node, Children: remainingChildren};
}

/**
 * Extract a sub-tree containing only the specified leaf IDs and their ancestor paths.
 * Returns null if no matching leaves exist in this branch.
 */
export function extractToC(node: ToCNode, idsToKeep: Set<string>): ToCNode | null {
    const isLeaf = !node.Children || node.Children.length === 0;

    if (isLeaf) {
        return idsToKeep.has(node.Id) ? {...node, Children: []} : null;
    }

    const keptChildren = node.Children
        .map((child) => extractToC(child, idsToKeep))
        .filter((child): child is ToCNode => child !== null);

    if (keptChildren.length === 0) return null;

    return {...node, Children: keptChildren};
}

/**
 * Merge multiple ToCNode root nodes into a single tree.
 * Deduplicates branches by Id — if two nodes share the same Id,
 * their children are merged recursively.
 */
export function mergeToC(nodes: (ToCNode | null)[]): ToCNode | null {
    const validNodes = nodes.filter((n): n is ToCNode => n !== null);
    if (validNodes.length === 0) return null;
    if (validNodes.length === 1) return validNodes[0];

    // All nodes being merged should share the same root Id (same law).
    // Use the first node as the base and merge children from all nodes.
    const base = validNodes[0];
    const allChildren: ToCNode[] = [];

    for (const node of validNodes) {
        for (const child of node.Children || []) {
            allChildren.push(child);
        }
    }

    // Deduplicate children by Id, recursively merging duplicates
    const childMap = new Map<string, ToCNode[]>();
    for (const child of allChildren) {
        const existing = childMap.get(child.Id) || [];
        existing.push(child);
        childMap.set(child.Id, existing);
    }

    const mergedChildren: ToCNode[] = [];
    for (const [, dupes] of childMap) {
        if (dupes.length === 1) {
            mergedChildren.push(dupes[0]);
        } else {
            const merged = mergeToC(dupes);
            if (merged) mergedChildren.push(merged);
        }
    }

    return {
        ...base,
        Children: mergedChildren,
    };
}